import { cosvcAiClient, apiClient, removeUndefinedKeys } from './clients';

// type
import {
  AiArticleType,
  GETv1AiArticlesReturnResponse,
  ResponseAiArticleType,
  ResponseAiArticleTypeV2,
  GETAiArticlesReturnResponseV2,
  GETAIGCHotResponse,
  ResponseAIGCHot,
  AiCategoryType,
  GETAIGCAnswerMetaResponse
} from '@fe-news/constants/ai/type';

/**
 * https://cosvc.api.beta.cnyes.cool/cosvc/api/v1/yushan_ai/article/summary/headline
 * @export 搜尋 AI 摘要新聞
 * @param {AIArticleType} type 類別
 * @returns {Promise<GETv1AIArticlesResponse>}
 */

const DEFAULT_AI_BASE_URL = '/cosvc/api/v1/yushan_ai/article/summary';
const DEFAULT_REVALIDATE_TIME = 600;

type GETAiNewsType = {
  type: AiArticleType;
};

export const GETAiNews = async ({ type }: GETAiNewsType): Promise<GETv1AiArticlesReturnResponse> => {
  const url = `${DEFAULT_AI_BASE_URL}/${type}`;

  try {
    const data = (await cosvcAiClient.get(url, {
      next: { revalidate: DEFAULT_REVALIDATE_TIME }
    })) as ResponseAiArticleType;

    const { items } = data;

    return {
      ...items,
      createdAt: items.created_at
    };
  } catch (error) {
    console.error(`[API Error]: GETAiNews ${type}`, error);
    throw error;
  }
};

/**
 * https://api.beta.cnyes.cool/media/api/v1/newslist/category
 * @export 搜尋 AI 摘要新聞
 * @param {NewsCategory} type 類別
 * @returns {Promise<GETAiArticlesReturnResponseV2>}
 */

type GETAiNewsV2Type = {
  type: AiCategoryType;
  page: number;
};

export const GETAiNewsV2 = async ({ type, page }: GETAiNewsV2Type): Promise<GETAiArticlesReturnResponseV2> => {
  const aiType = type === 'headline_ai' ? 'ai_headline' : type === 'tw_stock_ai' ? 'ai_tw_stock' : 'ai_wd_stock';

  const url = `/media/api/v1/newslist/category/${aiType}`;

  const params = {
    page,
    limit: 1
  };

  try {
    const data = (await apiClient.get(url, removeUndefinedKeys(params), {
      next: { revalidate: DEFAULT_REVALIDATE_TIME }
    })) as ResponseAiArticleTypeV2;

    return {
      ...data,
      items: {
        total: data.items.total,
        perPage: data.items.per_page,
        currentPage: data.items.current_page,
        lastPage: data.items.last_page,
        nextPageUrl: data.items.next_page_url,
        prevPageUrl: data.items.prev_page_url,
        from: data.items.from,
        to: data.items.to,
        data: data.items.data
      }
    };
  } catch (error) {
    console.error(`[API Error]: GETAiNews ${type} ${page}`, error);
    throw error;
  }
};

/**
 * https://aigc.cnyes.com/hot-qa.json
 * @export 摩速提供 AI 熱門問題
 * @returns {Promise<GETAIGCHotResponse>}
 */

export const GETAIGCHot = async (): Promise<GETAIGCHotResponse> => {
  try {
    const response = await fetch('https://aigc.cnyes.com/hot-qa.json');
    const data = (await response.json()) as ResponseAIGCHot;

    return {
      customerId: data.customer_id,
      generatedAt: data.generated_at,
      hotQa: data.hot_qa.map(item => ({
        createdAt: item.created_at,
        link: item.link,
        question: item.question,
        sourceUrl: item.source_url,
        views: item.views
      }))
    };
  } catch (error) {
    console.error(`[API Error]: GETAIGCHot`, error);
    throw error;
  }
};

/**
 * https://aigc.cnyes.com/answer/%E9%81%8B%E5%8B%95%E4%BC%91%E9%96%92%E9%9E%8B%E4%BB%A3-4-11995850/meta
 * @export 摩速提供 AI 回答頁面 metadata
 * @returns {Promise<GETAIGCAnswerMetaResponse>}
 */

export const GETAIGCAnswerMeta = async (id: string): Promise<GETAIGCAnswerMetaResponse> => {
  try {
    const response = await fetch(`https://aigc.cnyes.com/answer/${id}/meta`);
    const data = await response.json();

    // eslint-disable-next-line @typescript-eslint/naming-convention
    const { og_title, og_description, source_url, ...rest } = data;

    rest.ogTitle = og_title;
    rest.ogDescription = og_description;
    rest.sourceUrl = source_url;

    return rest as GETAIGCAnswerMetaResponse;
  } catch (error) {
    console.error(`[API Error]: GETAIGCAnswerMeta ${id}`, error);
    throw error;
  }
};
